import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Calendar, ShoppingBag, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [favs, setFavs] = useState([]);
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    if (user && user !== false) {
      api.get("/my/studio-bookings").then((r) => setBookings(r.data)).catch(() => {});
      api.get("/my/prop-orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/my/favorites").then((r) => setFavs(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="pt-32 text-center">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div data-testid="dashboard-page" className="pt-24">
      <section className="container mx-auto px-4 pt-12 pb-24">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-2">Dashboard</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-2">Hi, {user.name}.</h1>
        <p className="text-zinc-500 mb-10">Manage your bookings, orders and favorites.</p>

        <div className="flex gap-2 border-b border-zinc-200 mb-6">
          {[
            { id: "bookings", label: "Studio Bookings", icon: Calendar, count: bookings.length },
            { id: "orders", label: "Prop Orders", icon: ShoppingBag, count: orders.length },
            { id: "favorites", label: "Favorites", icon: Heart, count: favs.length },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dash-tab-${t.id}`}
                className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition ${
                  tab === t.id ? "border-[#FF007F] text-[#FF007F]" : "border-transparent text-zinc-500 hover:text-[#0A1128]"
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
                <span className="text-[10px] bg-zinc-200 rounded-full px-1.5 py-0.5">{t.count}</span>
              </button>
            );
          })}
        </div>

        {tab === "bookings" && (
          bookings.length === 0 ? <Empty msg="No bookings yet." cta={{ to: "/studios", label: "Book a studio" }} /> :
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white border border-zinc-200 rounded-2xl p-5 flex flex-wrap justify-between gap-4" data-testid={`dash-booking-${b.id}`}>
                <div>
                  <div className="font-display font-bold text-lg">{b.studio_name}</div>
                  <div className="text-sm text-zinc-500">{b.booking_date} · {b.time_slot} · {b.hours}h</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">₹{b.total_amount.toLocaleString()}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-700"}`}>
                    {b.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          orders.length === 0 ? <Empty msg="No orders yet." cta={{ to: "/props", label: "Browse props" }} /> :
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={`dash-order-${o.id}`}>
                <div className="flex justify-between mb-3">
                  <div className="font-mono text-xs text-zinc-500">#{o.id.slice(0, 8)}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-700"}`}>
                    {o.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {o.items.map((it) => (
                    <span key={it.prop_id} className="text-sm">{it.name} ×{it.quantity} ({it.rental_days}d)</span>
                  ))}
                </div>
                <div className="text-right font-bold">₹{o.total.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "favorites" && (
          favs.length === 0 ? <Empty msg="No favorites saved yet." cta={{ to: "/portfolio", label: "Discover work" }} /> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {favs.map((f, i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{f.type}</div>
                <div className="font-bold">{f.id}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Empty({ msg, cta }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-12 text-center">
      <p className="text-zinc-600 mb-4">{msg}</p>
      <Link to={cta.to} className="inline-flex px-5 py-2.5 rounded-full bg-[#0A1128] text-white text-sm font-semibold">{cta.label}</Link>
    </div>
  );
}
